<template>
	<div class="hg" @pointerdown.self="$emit('close')" @keydown.esc="$emit('close')">
		<div class="hg-box" role="dialog" aria-modal="true" aria-labelledby="hg-title">
			<p class="hg-step">{{ index + 1 }} of {{ steps.length }}</p>
			<h3 id="hg-title" ref="title" tabindex="-1">{{ step.title }}</h3>
			<div class="hg-body">
				<p v-if="step.intro" class="hg-intro">{{ step.intro }}</p>
				<section v-for="part in step.parts" :key="part.name" class="hg-part">
					<h4>{{ part.name }}</h4>
					<p v-if="part.what">{{ part.what }}</p>
					<ol v-if="part.flow" class="hg-flow">
						<li v-for="item in part.flow" :key="item">{{ item }}</li>
					</ol>
					<div v-if="part.bars" class="hg-bars">
						<div v-for="bar in part.bars" :key="bar[0]" class="hg-bar">
							<span class="hg-bar-name">{{ bar[0] }}</span>
							<span class="hg-bar-track"><span class="hg-bar-fill" :style="{ width: `${bar[1]}%` }" /></span>
							<span class="hg-bar-label">{{ bar[2] }}</span>
						</div>
					</div>
					<ul v-if="part.points" class="hg-points">
						<li v-for="item in part.points" :key="item[0]">
							<span>{{ item[0] }}</span><b>{{ item[1] }}</b>
						</li>
					</ul>
					<p v-if="part.example" class="hg-example">{{ part.example }}</p>
				</section>
			</div>
			<div class="hg-dots" aria-hidden="true">
				<span v-for="(s, i) in steps" :key="s.key" class="hg-dot" :class="{ 'hg-dot--on': i === index }" />
			</div>
			<div class="hg-actions">
				<button v-if="index > 0" type="button" class="hg-back" @click="go(index - 1)">Back</button>
				<button v-if="index < steps.length - 1" ref="next" type="button" class="hg-next" @click="go(index + 1)">Next</button>
				<button v-else ref="next" type="button" class="hg-next" @click="$emit('close')">Done</button>
				<button v-if="index < steps.length - 1" type="button" class="hg-skip" @click="$emit('close')">Close</button>
			</div>
		</div>
	</div>
</template>

<script>
import { durationLabel } from "./hudFormat";

// The real half-lives (seconds), for when the pipeline has not answered yet.
const DEFAULT_HALF_LIVES = { career: 50 * 86400, recent: 7 * 86400, views: 7 * 86400, momentum: 14 * 86400, section: 3 * 86400 };

export default {
	name: "HudHelpGuide",
	props: {
		halfLives: { type: Object, default: null },
		testMode: { type: Boolean, default: false },
	},
	data() {
		return { index: 0 };
	},
	computed: {
		half() {
			const given = this.halfLives || {};
			const out = {};
			Object.keys(DEFAULT_HALF_LIVES).forEach((name) => {
				out[name] = durationLabel(given[name] > 0 ? given[name] : DEFAULT_HALF_LIVES[name]);
			});
			return out;
		},
		steps() {
			const h = this.half;
			return [
				{
					key: "welcome",
					title: "What is this?",
					intro: "This panel shows what the site has learned about you from what you click.",
					parts: [
						{ name: "How it works", flow: ["You click jobs, courses and tests", "The site learns what you like", "You get better suggestions"] },
						{ name: "Buttons at the top", points: [["Open this guide again", "?"], ["Wipe everything and start fresh", "Bin"]] },
					],
				},
				{
					key: "profile",
					title: "Profile tab",
					intro: "What the site thinks you like. A longer bar means more interest.",
					parts: [
						{
							name: "Career direction",
							what: "The kind of job you like most.",
							bars: [["Software Development", 80, "80"], ["Data & Analytics", 20, "20"]],
							points: [["Apply to a job", "+20"], ["Buy a course or test", "+15"], ["Open a test", "+15"], ["Start applying", "+5"]],
							example: "Just looking at a job counts only after 3 looks in the same field.",
						},
						{
							name: "Right now",
							what: `What you looked at lately. It forgets fast: half is gone every ${h.recent}.`,
							example: "A coder who starts looking at data courses shows Data here first.",
						},
						{
							name: "Learning momentum",
							what: "How busy you are learning right now.",
							points: [["Buy a course or test", "+12"], ["Apply to a job", "+8"], ["Click enrol", "+6"], ["View a course", "+2"], ["View a test", "+1"]],
							example: "View 3 courses and click enrol once: 2 + 2 + 2 + 6 = 12.",
						},
						{ name: "Assessment confidence", what: "Not used yet." },
					],
				},
				{
					key: "decay",
					title: "Decay tab",
					intro: "Scores shrink when you stop using the site. Each time period, half of the score is gone.",
					parts: [
						{
							name: "Example: career score 40",
							bars: [["Now", 100, "40"], [`After ${h.career}`, 50, "20"], [`After ${h.career} more`, 25, "10"]],
							example: "Do something in that field again and the score goes back up.",
						},
						{
							name: "How fast each one shrinks (half gone every…)",
							points: [["Career direction", h.career], ["Right now and views", h.recent], ["Learning momentum", h.momentum], ["Home page sections", h.section]],
						},
						{
							name: "Home page sections",
							what: "Decides if Jobs or Courses comes first on your home page. The bigger one goes first.",
							bars: [["Jobs", 75, "first"], ["Courses", 25, "second"]],
						},
						this.testMode
							? { name: "Test mode is on", what: "Times are made very short so you can watch the bars shrink live. Each bar shows how much of the score is left, from 100% at your last click down to 0%." }
							: { name: "Looking ahead", what: "Pick a time away to see how much of each score would be left." },
					],
				},
				{
					key: "history",
					title: "History tab",
					intro: "A list of everything you did. Newest is on top.",
					parts: [
						{ name: "What one row looks like", points: [["Applied to a job", "+20 career"], ["Viewed a course", "+2 momentum"], ["Scrolled past a section", "+1 activity"]] },
						{ name: "Summary", what: "Press Summary at the top to see totals." },
					],
				},
				{
					key: "events",
					title: "Live events tab",
					intro: "Shows each click the moment it happens.",
					parts: [{ name: "Try it", flow: ["Click a job", "job.viewed shows here", "A few seconds later it moves to History"] }],
				},
				{
					key: "recommendations",
					title: "Recommendations tab",
					intro: "Jobs, courses and tests picked for you.",
					parts: [{ name: "Match", what: "A higher match means a better fit for you. A brand-new user sees none yet." }],
				},
			];
		},
		step() {
			return this.steps[this.index];
		},
	},
	mounted() {
		this.$nextTick(() => this.$refs.next && this.$refs.next.focus());
	},
	methods: {
		go(index) {
			this.index = Math.max(0, Math.min(this.steps.length - 1, index));
			this.$nextTick(() => this.$refs.title && this.$refs.title.focus());
		},
	},
};
</script>

<style scoped>
.hg {
	position: absolute;
	inset: 0;
	z-index: 3;
	/* Flex, not grid: a grid row grows with the box, so its max-height never applied. */
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	border-radius: 14px;
	background: rgba(26, 65, 100, 0.35);
}
.hg-box {
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 420px;
	max-height: 100%;
	min-height: 0;
	padding: 18px 18px 14px;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 12px 32px rgba(26, 65, 100, 0.25);
}
.hg-step {
	margin: 0 0 2px;
	font: 600 11px "Montserrat", sans-serif;
	letter-spacing: 0.04em;
	color: #72738e;
}
.hg-box h3 {
	margin: 0 0 8px;
	font: 700 16px "Montserrat", sans-serif;
	color: #1a4164;
	outline: none;
}
.hg-body {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	padding-right: 4px;
}
.hg-intro,
.hg-part p {
	margin: 0 0 8px;
	font-size: 13px;
	line-height: 1.55;
	color: #464667;
}
.hg-part {
	padding: 8px 0 2px;
	border-top: 1px solid #edf1f5;
}
.hg-part h4 {
	margin: 0 0 4px;
	font: 700 13px "Montserrat", sans-serif;
	color: #20639b;
}
.hg-example {
	padding: 7px 10px;
	border-radius: 8px;
	background: #f1f6fb;
}
.hg-flow {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin: 0 0 8px;
	padding: 0;
	list-style: none;
	counter-reset: hg-flow;
}
.hg-flow li {
	counter-increment: hg-flow;
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: #464667;
}
.hg-flow li::before {
	content: counter(hg-flow);
	display: grid;
	place-items: center;
	flex: none;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: #20639b;
	color: #fff;
	font: 700 11px "Montserrat", sans-serif;
}
.hg-points {
	margin: 0 0 8px;
	padding: 0;
	list-style: none;
}
.hg-points li {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	padding: 4px 0;
	font-size: 13px;
	color: #464667;
}
.hg-points b {
	flex: none;
	padding: 1px 8px;
	border-radius: 10px;
	background: #e8f1fa;
	color: #20639b;
	font: 700 12px "Montserrat", sans-serif;
}
.hg-bars {
	display: flex;
	flex-direction: column;
	gap: 5px;
	margin: 0 0 8px;
}
.hg-bar {
	display: grid;
	grid-template-columns: 11em 1fr 3.5em;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: #464667;
}
.hg-bar-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.hg-bar-track {
	height: 8px;
	border-radius: 4px;
	background: #edf1f5;
	overflow: hidden;
}
.hg-bar-fill {
	display: block;
	height: 100%;
	border-radius: 4px;
	background: #20639b;
}
.hg-bar-label {
	text-align: right;
	font-weight: 700;
	color: #1a4164;
}
.hg-dots {
	display: flex;
	justify-content: center;
	gap: 6px;
	margin: 10px 0;
}
.hg-dot {
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background: #d5dde6;
}
.hg-dot--on {
	background: #20639b;
}
.hg-actions {
	display: flex;
	gap: 8px;
}
.hg-back,
.hg-next,
.hg-skip {
	padding: 7px 14px;
	border-radius: 16px;
	font: 600 12.5px "Montserrat", sans-serif;
	cursor: pointer;
}
.hg-next {
	border: none;
	background: #20639b;
	color: #fff;
}
.hg-back {
	border: 1px solid #e2e8f0;
	background: #fff;
	color: #464667;
}
.hg-skip {
	margin-left: auto;
	border: none;
	background: none;
	color: #72738e;
}
.hg-back:focus-visible,
.hg-next:focus-visible,
.hg-skip:focus-visible {
	outline: 2px solid #4a7eff;
	outline-offset: 2px;
}
</style>
